import {useEffect} from 'react';

import {getDatadogRum} from './datadogRumFacade';

const lastLoggedFeatureFlagValues = new Map<string, boolean>();

/**
 * Sends a feature flag value to Datadog RUM when its effective value changes.
 *
 * Ramp hooks can run across many components and rerenders. Re-sending the same
 * value would add noise and SDK work without changing the current view's flag
 * state. The dedupe cache is reset when a new RUM view starts, so the same
 * value can be logged again if the flag evaluates in that view.
 *
 * @deprecated This should be called in recordFeatureFlag instead, the sanctioned way to log feature flag telemetry. It's marked deprecated to discourage direct usage pending a custom lint rule, but DO NOT DELETE THIS FUNCTION.
 */
export function recordRumFeatureFlagEvaluation(key: string, value: boolean) {
  const flagName = repairFeatureFlagName(key);

  if (
    lastLoggedFeatureFlagValues.has(flagName) &&
    Object.is(lastLoggedFeatureFlagValues.get(flagName), value)
  ) {
    return;
  }
  lastLoggedFeatureFlagValues.set(flagName, value);
  getDatadogRum().addFeatureFlagEvaluation(flagName, value);
}

/**
 * Runs after React commits so render stays free of Datadog side effects.
 *
 * @deprecated This should be called in recordFeatureFlag/useRecordFeatureFlag instead, the sanctioned way to log feature flag telemetry. It's marked deprecated to discourage direct usage pending a custom lint rule, but DO NOT DELETE THIS FUNCTION.
 */
export function useRecordRumFeatureFlagEvaluation(key: string, value: boolean) {
  useEffect(() => {
    recordRumFeatureFlagEvaluation(key, value);
  }, [key, value]);
}

/**
 * Starts each manual RUM view with an empty feature-flag dedupe cache.
 *
 * We intentionally do not replay cached flag values across views. Many ramp
 * hooks are scoped by entity or organization while RUM only stores the flag
 * name, so a previous view's value may describe a different scope.
 */
export function resetRumFeatureFlagEvalsForView() {
  lastLoggedFeatureFlagValues.clear();
}

/**
 * Datadog doesn't like feature flags with `.` in the name. If you log a
 * feature with `.` in the name, it gets rolled up with all the other flags
 * under the same prefix, and sets them all to the string `object Object`. To
 * avoid that, we fixup the feature flag name so that DD doesn't interpret it
 * as an object literal path.
 *
 * @param name - The feature flag name to repair
 * @returns The repaired feature flag name with dots replaced by double underscores
 */
function repairFeatureFlagName(name: string) {
  return name.replace(/\./g, '__');
}
